import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProgressMeter from './components/ProgressMeter';
import BasicInfoSection from './components/BasicInfoSection';
import LocationSection from './components/LocationSection';
import RelationshipStatusSection from './components/RelationshipStatusSection';
import BioSection from './components/BioSection';
import InterestsSection from './components/InterestsSection';
import PhotoUploadSection from './components/PhotoUploadSection';
import PrivacyControlsSection from './components/PrivacyControlsSection';
import ProfilePreviewPanel from './components/ProfilePreviewPanel';
import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../services/supabaseService';
import { useLocation } from 'react-router-dom';

const ProfileCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Get registration data from navigation state
  const registrationData = location?.state;

  const [formData, setFormData] = useState({
    displayName: '',
    age: '',
    city: '',
    relationshipStatus: '',
    lookingFor: '',
    bio: '',
    interests: [],
    photos: [],
    locationPrivacy: 'approximate',
    hideFromNearby: false,
    blurIntensity: 50,
    hideFace: true,
    requirePhotoApproval: true,
    messageAccess: 'verified',
    profileVisibility: 'limited',
    showOnlineStatus: true,
    appearInSearch: true,
    showLastActive: false,
    allowScreenshots: false
  });

  const [errors, setErrors] = useState({});
  const [completedSections, setCompletedSections] = useState([]);

  useEffect(() => {
    const completed = [];
    if (formData?.displayName && formData?.age) completed?.push('basic');
    if (formData?.city) completed?.push('location');
    if (formData?.relationshipStatus && formData?.lookingFor) completed?.push('relationship');
    if (formData?.bio && formData?.bio?.length >= 50) completed?.push('bio');
    if (formData?.interests?.length >= 3) completed?.push('interests');
    if (formData?.photos?.length >= 1) completed?.push('photos');
    setCompletedSections(completed);
  }, [formData]);

  const calculateCompletion = () => {
    return Math.round((completedSections?.length / 6) * 100);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePrivacyChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.displayName || formData?.displayName?.trim()?.length < 3) {
      newErrors.displayName = 'Display name must be at least 3 characters';
    }

    if (!formData?.age) {
      newErrors.age = 'Please select your age';
    }

    if (!formData?.city) {
      newErrors.city = 'Please select your city';
    }

    if (!formData?.relationshipStatus) {
      newErrors.relationshipStatus = 'Please select your relationship status';
    }

    if (!formData?.lookingFor) {
      newErrors.lookingFor = 'Please select what you\'re looking for';
    }

    if (!formData?.bio || formData?.bio?.trim()?.length < 50) {
      newErrors.bio = 'Bio must be at least 50 characters';
    }

    if (formData?.interests?.length < 3) {
      newErrors.interests = 'Please select at least 3 interests';
    }

    if (formData?.photos?.length < 1) {
      newErrors.photos = 'Please upload at least 1 photo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSaveProfile = async () => {
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!user) {
      setSaveError('You must be logged in to create a profile');
      navigate('/registration-login');
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      // Prepare profile data
      const profileData = {
        displayName: formData?.displayName,
        age: parseInt(formData?.age),
        city: formData?.city,
        relationshipStatus: formData?.relationshipStatus,
        lookingFor: formData?.lookingFor,
        bio: formData?.bio,
        interests: formData?.interests,
        photos: formData?.photos,
        photosAlt: formData?.photos?.map(() => 'User uploaded photo'),
        primaryPhoto: formData?.photos?.[0] || null,
        primaryPhotoAlt: 'User profile photo',
        privacyLevel: formData?.locationPrivacy === 'exact' ? 'low' : formData?.locationPrivacy === 'approximate' ? 'medium' : 'high',
        photoBlurred: formData?.hideFace || formData?.blurIntensity > 30,
        blurIntensity: formData?.blurIntensity > 60 ? 'high' : formData?.blurIntensity > 30 ? 'medium' : 'low',
        hideFace: formData?.hideFace,
        locationPrivacy: formData?.locationPrivacy,
        hideFromNearby: formData?.hideFromNearby,
        profileVisibility: formData?.profileVisibility,
        messageAccess: formData?.messageAccess,
        showOnlineStatus: formData?.showOnlineStatus,
        appearInSearch: formData?.appearInSearch,
        showLastActive: formData?.showLastActive,
        allowScreenshots: formData?.allowScreenshots,
        profileCompletion: calculateCompletion()
      };

      await profileService?.updateProfile(user?.id, profileData);
      await updateProfile(profileData);

      // Verify user is still authenticated before navigating
      if (user) {
        // Small delay to ensure profile update is complete
        setTimeout(() => {
          navigate('/member-dashboard');
        }, 100);
      } else {
        // User not authenticated, redirect to login
        setSaveError('Your session has expired. Please sign in again.');
        setTimeout(() => {
          navigate('/registration-login');
        }, 2000);
      }
    } catch (error) {
      console.error('Profile save error:', error);
      setSaveError(error?.message || 'Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreviewProfile = () => {
    setShowPreview(true);
  };

  const steps = [
    { id: 1, label: 'Basic Info', component: BasicInfoSection },
    { id: 2, label: 'Location', component: LocationSection },
    { id: 3, label: 'Relationship', component: RelationshipStatusSection },
    { id: 4, label: 'About You', component: BioSection },
    { id: 5, label: 'Interests', component: InterestsSection },
    { id: 6, label: 'Photos', component: PhotoUploadSection },
    { id: 7, label: 'Privacy', component: PrivacyControlsSection }
  ];

  return (
    <>
      <Helmet>
        <title>Create Your Profile - Bamchik</title>
        <meta name="description" content="Set up your anonymous profile with privacy-first controls on Bamchik discreet dating platform" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        {saveError && (
          <div className="mx-auto max-w-[1200px] px-4 lg:px-6 pt-6">
            <div className="p-4 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
              {saveError}
            </div>
          </div>
        )}

        <main className="mx-auto max-w-[1400px] px-4 lg:px-6 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <button
              onClick={() => navigate('/registration-login')}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth mb-4"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Registration
            </button>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold mb-2">
              Create Your Profile
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Set up your anonymous profile with complete privacy control
            </p>
          </div>

          <div className="mb-6">
            <ProgressMeter
              completionPercentage={calculateCompletion()}
              completedSections={completedSections}
              totalSections={6}
            />
          </div>

          <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="lg:hidden">
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {steps?.map((step) => (
                    <button
                      key={step?.id}
                      onClick={() => setCurrentStep(step?.id)}
                      className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-caption font-medium transition-smooth ${
                        currentStep === step?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground hover:bg-muted/80'
                      }`}
                    >
                      {step?.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="hidden lg:block space-y-6">
                <BasicInfoSection
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                />

                <LocationSection
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  onPrivacyChange={handlePrivacyChange}
                />

                <RelationshipStatusSection
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                />

                <BioSection
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                />

                <InterestsSection
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                />

                <PhotoUploadSection
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  onPrivacyChange={handlePrivacyChange}
                />

                <PrivacyControlsSection
                  formData={formData}
                  onPrivacyChange={handlePrivacyChange}
                />
              </div>

              <div className="lg:hidden">
                {currentStep === 1 && (
                  <BasicInfoSection
                    formData={formData}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                )}
                {currentStep === 2 && (
                  <LocationSection
                    formData={formData}
                    errors={errors}
                    onChange={handleInputChange}
                    onPrivacyChange={handlePrivacyChange}
                  />
                )}
                {currentStep === 3 && (
                  <RelationshipStatusSection
                    formData={formData}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                )}
                {currentStep === 4 && (
                  <BioSection
                    formData={formData}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                )}
                {currentStep === 5 && (
                  <InterestsSection
                    formData={formData}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                )}
                {currentStep === 6 && (
                  <PhotoUploadSection
                    formData={formData}
                    errors={errors}
                    onChange={handleInputChange}
                    onPrivacyChange={handlePrivacyChange}
                  />
                )}
                {currentStep === 7 && (
                  <PrivacyControlsSection
                    formData={formData}
                    onPrivacyChange={handlePrivacyChange}
                  />
                )}

                <div className="flex gap-3 mt-6">
                  {currentStep > 1 && (
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(prev => prev - 1)}
                      iconName="ChevronLeft"
                      iconPosition="left"
                    >
                      Previous
                    </Button>
                  )}
                  {currentStep < steps?.length && (
                    <Button
                      variant="default"
                      fullWidth
                      onClick={() => setCurrentStep(prev => prev + 1)}
                      iconName="ChevronRight"
                      iconPosition="right"
                    >
                      Next
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  variant="outline"
                  fullWidth
                  iconName="Eye"
                  iconPosition="left"
                  onClick={handlePreviewProfile}
                >
                  Preview Profile
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  iconName="Save"
                  iconPosition="left"
                  onClick={handleSaveProfile}
                  loading={isSaving}
                  disabled={calculateCompletion() < 100}
                >
                  {isSaving ? 'Saving Profile...' : 'Save & Continue'}
                </Button>
              </div>

              {calculateCompletion() < 100 && (
                <div className="p-4 bg-warning/10 rounded-lg flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-caption font-semibold text-warning mb-1">
                      Profile Incomplete
                    </p>
                    <p className="text-xs text-foreground">
                      Complete all sections to activate your profile and start connecting with others
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="hidden lg:block">
              <ProfilePreviewPanel formData={formData} />
            </div>
          </div>
        </main>

        {showPreview && (
          <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-background/95">
            <div className="w-full max-w-md">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-heading font-semibold">Profile Preview</h2>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 rounded-lg hover:bg-muted transition-smooth"
                >
                  <Icon name="X" size={24} />
                </button>
              </div>
              <ProfilePreviewPanel formData={formData} />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProfileCreation;